import React from 'react';
import { ActivityResult } from '../../hooks/useVocabularyTraining';
import { ActivityType } from '../../types/vocabulary';
import { X, Check, ArrowLeft, BookOpen } from 'lucide-react';

interface TrainingReviewProps {
  results: ActivityResult[];
  onBack: () => void;
}

const getActivityName = (activityType: ActivityType): string => {
  return activityType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const TrainingReview: React.FC<TrainingReviewProps> = ({ results, onBack }) => {
  const incorrectResults = results.filter(r => !r.success);

  if (incorrectResults.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Perfect Score!</h2>
          <p className="text-gray-600 mb-6">You didn't make any mistakes. Great job!</p>
          <button
            onClick={onBack}
            className="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors"
          >
            Back to Summary
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-purple-600" />
                  Review Your Mistakes
                </h1>
                <p className="text-gray-600 mt-1">
                  {incorrectResults.length} {incorrectResults.length === 1 ? 'mistake' : 'mistakes'} to review
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Error List */}
        <div className="space-y-4">
          {incorrectResults.map((result, index) => (
            <div
              key={`${result.wordId}-${result.activityType}-${result.timestamp}`}
              className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                        {getActivityName(result.activityType)}
                      </span>
                      <span className="text-sm text-gray-500">#{index + 1}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">
                      Word: <span className="text-purple-600">{result.word}</span>
                    </h3>
                  </div>
                  <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-full">
                    <X className="w-5 h-5 text-red-600" />
                  </div>
                </div>

                {/* Your Answer */}
                {result.userAnswer && (
                  <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm font-semibold text-red-700 mb-1">Your Answer:</p>
                    <p className="text-gray-800 font-medium">{result.userAnswer}</p>
                  </div>
                )}

                {/* Correct Answer */}
                {result.correctAnswer && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm font-semibold text-green-700 mb-1">Correct Answer:</p>
                    <p className="text-gray-800 font-medium">{result.correctAnswer}</p>
                  </div>
                )}

                {/* Explanation based on activity type */}
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-semibold text-blue-700 mb-1">Tip:</p>
                  <p className="text-gray-700 text-sm">
                    {getActivityTip(result.activityType, result.word)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button
            onClick={onBack}
            className="px-8 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors"
          >
            Back to Summary
          </button>
        </div>
      </div>
    </div>
  );
};

function getActivityTip(activityType: ActivityType, word: string): string {
  switch (activityType) {
    case ActivityType.Translation:
      return `Remember the translation for "${word}". Try using it in a sentence to reinforce your memory.`;
    case ActivityType.ListenToWrite:
      return `Listen carefully to the pronunciation. Practice saying "${word}" out loud to improve your listening skills.`;
    case ActivityType.SentenceFormation:
      return `Try to create a sentence using "${word}". Think about how it's used in context.`;
    case ActivityType.Synonyms:
      return `Think about words that have similar meanings to "${word}". Consider the context where each synonym might be used.`;
    case ActivityType.ImageLinking:
      return `Visualize what "${word}" represents. Try to connect the word with a mental image.`;
    case ActivityType.SpeakWord:
      return `Practice pronouncing "${word}" correctly. Pay attention to the sounds and rhythm.`;
    case ActivityType.SpeakPhrase:
      return `Focus on the pronunciation and intonation. Practice speaking the phrase naturally.`;
    case ActivityType.ListenAndArrange:
      return `Listen carefully to the word order. Pay attention to how words flow together in the sentence.`;
    default:
      return `Keep practicing "${word}" to improve your understanding.`;
  }
}
